/* eslint-disable no-return-assign */
/* eslint-disable import/no-extraneous-dependencies */
import { createRef, forwardRef } from "react";
import { PropTypes } from "prop-types";
import { TextField } from '@gvlab/react-theme/mui';
import clsx from "clsx";
import { KEYBOARD_KEY } from "./event";
import { 
  getFilledArray,
  joinArrayStrings,
  mergeArrayStringFromIndex,
  updateIndex
} from "./array";
import { split } from "./strings";

const OtpInput = forwardRef(
  (props, propRef) => {
    const {
      length,
      value,
      autoFocus,
      onChange,
      TextFieldsProps,
      onComplete,
      validateChar,
      className,
      ...restProps
    } = props;

    const {
      onPaste,
      onFocus,
      onKeyDown,
      className: TextFieldClassName,
      ...restTextFieldsProps
    } = TextFieldsProps || {}

    const valueSplitted = getFilledArray(
      length,
      (_, index) => {
        return {
          character: (value)[index] || '-',
          inputRef: createRef(<input />)
        }
      }
    )

    const getIndexByInputElement = (inputElement) => {
      return valueSplitted.findIndex(({ inputRef }) => {
        return inputRef.current === inputElement
      })
    }

    const getCharactersSplitted = () => {
      return valueSplitted.map(({ character }) => {
        return character
      })
    }

    const replaceCharOfValue = (charIndex, charValue) => {
      const newValueSplitted = updateIndex(
        getCharactersSplitted(),
        charIndex,
        charValue
      )
      return joinArrayStrings(newValueSplitted)
    }

    const focusInputByIndex = (inputIndex) => {
      valueSplitted[inputIndex]?.inputRef.current?.focus()
    }

    const selectInputByIndex = (inputIndex) => {
      if (inputIndex === -1) {
        valueSplitted[valueSplitted.length - 1]?.inputRef.current?.select()
      } else {
        valueSplitted[inputIndex]?.inputRef.current?.select()
      }
    }

    const manageCaretForNextInput = (currentInputIndex) => {
      if (currentInputIndex + 1 === length) {
        return
      }
      if (valueSplitted[currentInputIndex + 1].character) {
        selectInputByIndex(currentInputIndex + 1)
      } else {
        focusInputByIndex(currentInputIndex + 1)
      }
    }


    const manageCaretForPreviousInput = (currentInputIndex) => {
      if (currentInputIndex > 0) {
        selectInputByIndex(currentInputIndex - 1)
      }
    }

    const handleOneInputChange = (
      event
    ) => {
      const character = event.target.value[0] || ''
      const currentInputIndex = getIndexByInputElement(event.target)

      if (
        typeof validateChar === 'function' &&
        !validateChar(character, currentInputIndex)
      ) {
        return
      }

      const newValue = replaceCharOfValue(currentInputIndex, character)

      onChange?.(newValue)

      if (newValue.length === length) {
        onComplete?.(newValue)
      }

      if (character !== '') {
        if (newValue.length < (length)) {
          manageCaretForNextInput(newValue.length - 1)
        } else {
          manageCaretForNextInput(currentInputIndex)
        }
      } else if (newValue[currentInputIndex]) {
        selectInputByIndex(currentInputIndex)
      } else {
        manageCaretForPreviousInput(currentInputIndex)
      }
    }

    const handleOneInputFocus = (event) => {
      event.preventDefault()
      event.target.select()
      onFocus?.(event)
    }

    const handleOneInputKeyDown = (
      event
    ) => {
      const inputElement = event.target
      const currentInputIndex = getIndexByInputElement(inputElement)
      if (inputElement.value === event.key) {
        event.preventDefault()
        manageCaretForNextInput(currentInputIndex)
      } else if (!inputElement.value && KEYBOARD_KEY.backspace === event.key) {
        event.preventDefault()
        selectInputByIndex(currentInputIndex - 1)
      } else if (KEYBOARD_KEY.left === event.key) {
        event.preventDefault()
        selectInputByIndex(currentInputIndex - 1)
      } else if (KEYBOARD_KEY.right === event.key) {
        event.preventDefault()
        selectInputByIndex(currentInputIndex + 1)
      } else if (KEYBOARD_KEY.home === event.key) {
        event.preventDefault()
        selectInputByIndex(0)
      } else if (KEYBOARD_KEY.end === event.key) {
        event.preventDefault()
        selectInputByIndex(-1)
      }
      onKeyDown?.(event)
    }

    const handleOneInputPaste = (
      event
    ) => {
      event.preventDefault()
      const inputElement = event.target
      const content = event.clipboardData.getData('text/plain')
      const currentInputIndex = getIndexByInputElement(inputElement)
      const currentCharacter = getCharactersSplitted()
      const characters = mergeArrayStringFromIndex(
        currentCharacter,
        split(content),
        currentInputIndex
      )
      const characterIndexEmpty = characters.findIndex((character, index) => {
        return index > currentInputIndex && character === ''
      })

      const newValue = joinArrayStrings(characters)
      onChange?.(newValue)
      if (newValue.length === length) {
        onComplete?.(newValue)
        focusInputByIndex(length - 1)
        return
      }

      if (characterIndexEmpty !== -1) {
        focusInputByIndex(characterIndexEmpty)
      }
      onPaste?.(event)
    }

    return (
      <div className="flex gap-2 items-center	" ref={propRef} {...restProps}>
        {valueSplitted.map(({ character, inputRef }, index) => {
          return (
            <TextField
              autoFocus={autoFocus ? index === 0 : false}
              autoComplete="one-time-code"
              value={character}
              inputRef={inputRef}
              className={clsx('text-center', `MuiOtpInput-TextField`, ` MuiOtpInput-TextField-${ index + 1 }`, `${TextFieldClassName || ''}`)}
              inputProps={{ maxLength: 1, style: { textAlign: 'center' }  }}
              onPaste={handleOneInputPaste}
              onFocus={handleOneInputFocus}
              onChange={handleOneInputChange}
              onKeyDown={handleOneInputKeyDown}
              key={index}
              {...restTextFieldsProps}
            />
          );
        })}
      </div>
    );
  }
);

OtpInput.defaultProps = {
  length: 6,
  value: '',
  onChange: () => {},
  onComplete: () => {},
  autoFocus: false,
  validateChar: () => true,
  className: '',
  TextFieldsProps: {},
};

OtpInput.propTypes = {
  length: PropTypes.number,
  value: PropTypes.string,
  validateChar: PropTypes.func,
  onChange: PropTypes.func,
  onComplete: PropTypes.func,
  className: PropTypes.string,
  TextFieldsProps: PropTypes.object,
};
  
export default OtpInput;
